import React from 'react';
import { useTheme } from '../../context/ThemeContext';

interface AxisFailurePopupProps {
  message: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  onRetry?: () => void;
}

export const AxisFailurePopup: React.FC<AxisFailurePopupProps> = ({ message, isOpen, onClose, title, onRetry }) => {
  const { theme } = useTheme();

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;

  const sanitizeMessage = (raw: string): string => {
    if (!raw) return 'Please try again. If the issue persists, contact support.';
    const msg = raw
      .replace(/400 Bad Request.*/i, '')
      .replace(/500 Internal Server Error.*/i, '')
      .replace(/https?:\/\/\S+/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Friendly mappings
    if (/expecting type date/i.test(raw) || /invalid\.?\s*date/i.test(raw)) {
      return 'Please check the date of birth format (YYYY-MM-DD).';
    }
    if (/unauthorized|forbidden|permission/i.test(raw)) {
      return 'You do not have permission to perform this action.';
    }
    if (/not eligible/i.test(raw)) {
      return 'You are not currently eligible to schedule this appointment.';
    }
    if (/network|timeout|fetch failed|connect/i.test(raw)) {
      return 'Network issue detected. Please check your connection and try again.';
    }
    if (/internal server error|service unavailable|bad gateway|gateway timeout/i.test(raw)) {
      return 'We are experiencing issues right now. Please try again shortly.';
    }
    if (/bad request|validation/i.test(raw)) {
      return 'Some information looks incorrect. Please review and try again.';
    }
    return msg || 'Something went wrong. Please try again.';
  };

  const inferredTitle = title
    || (/unauthorized|forbidden/i.test(message) ? 'Action Not Allowed'
      : /not eligible/i.test(message) ? 'Not Eligible'
      : /internal server error|service unavailable/i.test(message) ? 'We ran into an issue'
      : /bad request|validation/i.test(message) ? 'Check your information'
      : 'Something went wrong');

  const userMessage = sanitizeMessage(message);
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 modal-backdrop"
      onClick={handleBackdropClick}
      style={{ pointerEvents: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="failure-popup-title"
      aria-describedby="failure-popup-desc"
    >
      <div 
        className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full modal-content relative"
        onClick={(e) => e.stopPropagation()}
        style={{ pointerEvents: 'auto' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-xl p-1"
          aria-label="Close"
          style={{ pointerEvents: 'auto' }}
        >
          ×
        </button>
        
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
        </div>
        
        <h2 id="failure-popup-title" className="text-xl font-bold text-center mb-2">{inferredTitle}</h2>
        <p id="failure-popup-desc" className="text-gray-700 text-center mb-6">{userMessage}</p>
        
        <div className="flex justify-center gap-3">
          {onRetry && (
            <button 
              onClick={onRetry}
              className={`px-5 py-2 rounded-md text-white button-hover ${theme['button-bg']}`}
              style={{ pointerEvents: 'auto' }}
            >
              Try Again
            </button>
          )}
          <button 
            onClick={onClose}
            className="px-5 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            style={{ pointerEvents: 'auto' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
